import React from 'react';
import { CreditCard, TrendingUp, Percent, Shield, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета ОСАГО
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  return (
    <ToolResult title="Результаты расчета ОСАГО">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Базовая стоимость полиса */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <Shield className="w-4 h-4"/>
            Базовая стоимость ОСАГО
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatCurrency(results.basePremium)}
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            Стоимость обязательного страхования по базовым параметрам
          </p>
        </div>

        {/* Итоговая стоимость полиса */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <CreditCard className="w-4 h-4"/>
            Итоговая стоимость полиса
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatCurrency(results.totalPremium)}
            </span>
            {results.additionalPremium > 0 && (
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                (включая доп. опции: {formatCurrency(results.additionalPremium)})
              </span>
            )}
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Полная стоимость с учетом всех параметров и опций
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Базовый тариф" 
          value={formatCurrency(results.baseRate)} 
          color="emerald" 
          icon={<Shield className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Территориальный коэффициент" 
          value={formatNumber(results.territoryFactor, 2)} 
          color="blue" 
          icon={<TrendingUp className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Коэффициент возраста и стажа" 
          value={formatNumber(results.ageExperienceFactor, 2)} 
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="КБМ (бонус-малус)" 
          value={formatNumber(results.kbmFactor, 2)} 
          color={results.kbmFactor < 1 ? "green" : results.kbmFactor > 1 ? "red" : "gray"}
          icon={<Percent className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Коэффициент периода использования" 
          value={formatNumber(results.periodFactor, 2)} 
        />
        
        <ResultItem 
          label="Коэффициент аварийности" 
          value={formatNumber(results.accidentFactor, 2)} 
          color={results.accidentFactor > 1 ? "red" : "green"} 
        />
        
        <ResultItem 
          label="Коэффициент возраста ТС" 
          value={formatNumber(results.carAgeFactor, 2)} 
        />
        
        <ResultItem 
          label="Базовая премия ОСАГО" 
          value={formatCurrency(results.basePremium)} 
          color="emerald" 
        />
        
        {results.additionalPremium > 0 && (
          <ResultItem 
            label="Стоимость дополнительных опций" 
            value={formatCurrency(results.additionalPremium)} 
            color="indigo" 
            icon={<CheckCircle className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Итоговая стоимость полиса" 
          value={formatCurrency(results.totalPremium)} 
          color="blue" 
          size="lg"
          icon={<CreditCard className="w-5 h-5"/>} 
        />

        {/* Информация о ОСАГО */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Важно знать о тарифах ОСАГО</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Страховые компании могут устанавливать базовые ставки в пределах тарифного коридора, определенного Центробанком РФ.
                Итоговая стоимость полиса в разных компаниях может отличаться в пределах ±20% от расчетной суммы.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;